import React, { useState, useEffect } from 'react';
import {
    Grid,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Container,
    Card,
    CardContent,
    CardHeader,
    Box,
    Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { MASTER_FIELDS_MAPPING, employmentTypes } from '../../common/constants';
import { useSearchParams } from 'react-router-dom';

const StyledContainer = styled(Container)(({ theme }) => ({
    padding: '0 !important',
    maxWidth: '1200px',
    border: 'none',
}));

const StyledCard = styled(Card)(({ theme }) => ({
    marginBottom: 0,
    boxShadow: 'none',
    borderBottom: 'none',
    borderTop: 'none',
    borderRadius: 0,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
        },
    },
}));

const ViewModeContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
}));

const ViewModeLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    fontWeight: 500,
}));

const ViewModeValue = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: '1rem',
    fontWeight: 500,
}));

// Dynamic Field Component
const DynamicField = ({ disabled, field, value, onChange, error, touched, isEditable }) => {
    const fieldName = field.fieldLabel;
    const isRequired = field.isRequired;
    const hasOptions = field.fieldListOptions && field.fieldListOptions.length > 0;
    const fieldType = field?.fieldDataType === 'String' ? 'text' : field?.fieldDataType === 'Integer' ? 'number' : '';

    // View mode display
    if (!isEditable) {
        const displayValue = value || '-';
        return (
            <ViewModeContainer>
                <ViewModeLabel>{fieldName}</ViewModeLabel>
                <ViewModeValue>{displayValue}</ViewModeValue>
            </ViewModeContainer>
        );
    }

    // Edit mode display
    if (hasOptions) {
        // Dropdown field
        return (
            <FormControl fullWidth error={!!error} size="small">
                <InputLabel id={`${fieldName}-label`}>
                    {fieldName} {isRequired && '*'}
                </InputLabel>
                <Select
                    labelId={`${fieldName}-label`}
                    id={fieldName}
                    value={value || ''}
                    label={`${fieldName}`}
                    onChange={(e) => onChange(fieldName, e.target.value)}
                >
                    <MenuItem value="">
                        <em>Select an option</em>
                    </MenuItem>
                    {field.fieldListOptions.map((option, index) => {
                        return (
                            <MenuItem key={index} value={option}>
                                {option}
                            </MenuItem>
                        );
                    })}
                </Select>
                {error && <FormHelperText>{error}</FormHelperText>}
            </FormControl>
        );
    } else {
        // Text field
        return (
            <StyledTextField
                fullWidth
                label={`${fieldName}`}
                value={value || ''}
                onChange={(e) => onChange(fieldName, e.target.value)}
                error={!!error}
                helperText={error || ''}
                required={isRequired}
                size="small"
                type={fieldType}
            />
        );
    }
};

// Dynamic Form Component
const DynamicMetaFieldsForm = ({ preData, data, isLoading, preCreationHQPositionData, onDataChange, errors }) => {
    const [touched, setTouched] = useState({});
    const metaCustomFieldDefinitions = preCreationHQPositionData?.extendedDef?.metaCustomFieldDefinitions;
    const [searchParams] = useSearchParams();
    const action = searchParams.get('action') || 'view';
    const isEditable = action === 'edit';

    // Initialize form values and report to parent
    useEffect(() => {
        if (metaCustomFieldDefinitions) {
            const initialValues = {};
            const customFieldsData = preData?.internalHqProperties?.customFieldsData;
            metaCustomFieldDefinitions.forEach((field) => {
                const preHQValue = getPreHQData(field?.masterCustomAttribute?.attributeName);
                initialValues[field.fieldLabel] = preHQValue || customFieldsData?.[field.fieldLabel] || '';
            });

            onDataChange(initialValues);
        }
    }, [metaCustomFieldDefinitions, preData, onDataChange]);

    const handleFieldChange = (fieldName, value) => {
        const newFormValues = {
            ...data,
            [fieldName]: value,
        };
        onDataChange(newFormValues);

        // Mark field as touched
        setTouched((prev) => ({
            ...prev,
            [fieldName]: true,
        }));

        // Validate field
        validateField(fieldName, value);
    };

    const validateField = (fieldName, value) => {
        const field = metaCustomFieldDefinitions.find((f) => f.fieldLabel === fieldName);
        if (field && field.isRequired && (!value || value.trim() === '')) {
            // setErrors((prev) => ({
            //     ...prev,
            //     [fieldName]: `${fieldName} is required`,
            // }));
        } else {
            // setErrors((prev) => ({
            //     ...prev,
            //     [fieldName]: '',
            // }));
        }
    };

    const getPreHQData = (customAttributeName) => {
        if (!customAttributeName || !preData) return null;
        const fieldKey = MASTER_FIELDS_MAPPING[customAttributeName];

        if (fieldKey === 'experience') {
            const expText = `${preData?.minExperience}-${preData?.maxExperience}`;
            return expText;
        }

        if (fieldKey === 'skills') {
            const skillsText = preData?.mandatorySkills?.map((skill) => skill).join(', ');
            return skillsText;
        }

        if (fieldKey === 'description') {
            const descriptionHtml = preData?.description;
            if (!descriptionHtml) return '';
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = descriptionHtml;
            return tempDiv.textContent || tempDiv.innerText || '';
        }

        if (fieldKey === 'employmentType') {
            const employmentTypeValue = preData?.[fieldKey]; // This is the value, e.g., 'full-time'
            // Find the full object to ensure it's a valid type, but return the raw value
            const foundOption = employmentTypes.find((opt) => opt.value === employmentTypeValue);
            // Return the value that the dynamic dropdown expects, not the label.
            return foundOption ? foundOption.value : '';
        }

        if (fieldKey) {
            const preHQValue = preData?.[fieldKey];
            return preHQValue;
        }

        return null;
    };

    const filteredCustomFields = metaCustomFieldDefinitions?.filter(
        (item) => !MASTER_FIELDS_MAPPING[item.masterCustomAttribute?.attributeName],
    );

    return (
        <StyledContainer maxWidth="xl">
            <StyledCard>
                <CardHeader title="Custom Attributes" subheader="Additional information for the position" />
                <CardContent>
                    {!filteredCustomFields || filteredCustomFields.length === 0 ? (
                        <Typography variant="body1" color="text.secondary">
                            {isLoading ? 'Loading...' : 'No custom fields available'}
                        </Typography>
                    ) : (
                        <Grid container spacing={4}>
                            {filteredCustomFields.map((field, index) => (
                                <Grid item xs={12} md={6} key={index} sx={{ mb: 2 }}>
                                    <DynamicField
                                        disabled={!isEditable}
                                        field={field}
                                        value={
                                            (touched[field.fieldLabel] ? data?.[field.fieldLabel] : null) ||
                                            getPreHQData(field?.masterCustomAttribute?.attributeName) ||
                                            data?.[field.fieldLabel] ||
                                            ''
                                        }
                                        onChange={handleFieldChange}
                                        error={errors?.[field.fieldLabel]}
                                        touched={touched[field.fieldLabel]}
                                        isEditable={isEditable}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </CardContent>
            </StyledCard>
        </StyledContainer>
    );
};

export default DynamicMetaFieldsForm;
